import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { currencyFormat } from "../helpers.js";
import {
  RD_MAX_RATE,
  RD_MIN_RATE,
  rdMaxAmount,
  rdMinAmount,
  summarizeTeam,
} from "../data/companyTeamDemo.js";
import {
  inferSourceKind,
  normalizeOpening,
  normalizeSources,
  normalizeUrl,
} from "../utils/companyJobPostings.js";
import { requestCompanyTeam } from "../hooks/useCompanyTeamAllocation.js";
import CompanyTeamAllocationPanel from "./CompanyTeamAllocationPanel.jsx";

const INPUT_CLASS =
  "w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 transition-colors";

const UPGRADED_FALLBACK_LOGO = "/upgraded-collapsed-logo.png";

const formatSalary = (salary) => {
  if (typeof salary !== "number" || Number.isNaN(salary)) return "—";
  return currencyFormat(salary);
};

const RdBadge = ({ isRd }) => (
  <span
    className={[
      "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
      isRd ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600",
    ].join(" ")}
  >
    {isRd ? "Likely R&D" : "Other"}
  </span>
);

const CompanyLogoMark = ({ logoUrl, companyName }) => {
  const [src, setSrc] = useState(logoUrl || UPGRADED_FALLBACK_LOGO);

  useEffect(() => {
    setSrc(logoUrl || UPGRADED_FALLBACK_LOGO);
  }, [logoUrl]);

  const isFallback = src === UPGRADED_FALLBACK_LOGO;

  return (
    <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl border border-gray-100 bg-white p-2">
      <img
        src={src}
        alt={isFallback ? "Upgraded" : companyName || "Company logo"}
        className="h-full w-full object-contain"
        onError={() => setSrc(UPGRADED_FALLBACK_LOGO)}
      />
    </div>
  );
};

function CompanyTeamSection({ company }) {
  const [openings, setOpenings] = useState([]);
  const [jobSources, setJobSources] = useState([]);
  const [suggestedCareersUrl, setSuggestedCareersUrl] = useState("");
  const [sourceDraft, setSourceDraft] = useState("");
  const [domain, setDomain] = useState("");
  const [isOpeningsLoading, setIsOpeningsLoading] = useState(true);
  const [isOpeningsRefreshing, setIsOpeningsRefreshing] = useState(false);
  const [isIndexingOpenings, setIsIndexingOpenings] = useState(false);
  const [isSavingSources, setIsSavingSources] = useState(false);
  const [openingsError, setOpeningsError] = useState("");
  const [openingsMessage, setOpeningsMessage] = useState(null);

  const loadOpenings = useCallback(
    async ({ signal, isManual = false } = {}) => {
      if (!company?.id) {
        setOpenings([]);
        setJobSources([]);
        setIsOpeningsLoading(false);
        return;
      }

      const url = `/api/companyJobPostings?companyId=${encodeURIComponent(company.id)}`;
      if (isManual) {
        setIsOpeningsRefreshing(true);
        setOpeningsMessage({ type: "info", text: "Loading open roles from Airtable…" });
      } else {
        setIsOpeningsLoading(true);
      }
      setOpeningsError("");

      try {
        const data = await requestCompanyTeam(url, { signal });
        setJobSources(normalizeSources(data.sources || []));
        setOpenings(
          (data.openings || []).map((row) => normalizeOpening(row)).filter(Boolean)
        );
        setSuggestedCareersUrl(data.suggestedCareersUrl || "");
        if (data.domain) {
          setDomain((current) => current || data.domain);
        }

        if (isManual) {
          const count = (data.openings || []).length;
          setOpeningsMessage({
            type: "success",
            text: count
              ? `Loaded ${count} open ${count === 1 ? "role" : "roles"}.`
              : "No open roles saved yet. Add a careers URL and Index openings.",
          });
        }
      } catch (error) {
        if (error.name === "AbortError") return;
        setOpeningsError(error.message);
        if (isManual) setOpeningsMessage(null);
      } finally {
        if (!signal?.aborted) {
          setIsOpeningsLoading(false);
          setIsOpeningsRefreshing(false);
        }
      }
    },
    [company?.id]
  );

  useEffect(() => {
    const controller = new AbortController();
    loadOpenings({ signal: controller.signal });
    return () => controller.abort();
  }, [loadOpenings]);

  const persistJobSources = useCallback(
    async (nextSources) => {
      if (!company?.id) return;
      setIsSavingSources(true);
      try {
        const data = await requestCompanyTeam("/api/companyJobPostings", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ companyId: company.id, sources: nextSources }),
        });
        setJobSources(normalizeSources(data.sources || nextSources));
        if (data.openings) {
          setOpenings(
            (data.openings || []).map((row) => normalizeOpening(row)).filter(Boolean)
          );
        }
        setOpeningsMessage({ type: "success", text: "Job posting sources saved." });
      } catch (error) {
        setOpeningsMessage({ type: "error", text: error.message });
      } finally {
        setIsSavingSources(false);
      }
    },
    [company?.id]
  );

  const handleAddJobSource = async () => {
    const url = normalizeUrl(sourceDraft);
    if (!url) {
      setOpeningsMessage({ type: "error", text: "Enter a careers or jobs page URL." });
      return;
    }
    const next = normalizeSources([
      ...jobSources,
      { url, label: inferSourceKind(url), kind: inferSourceKind(url) },
    ]);
    setSourceDraft("");
    setJobSources(next);
    await persistJobSources(next);
  };

  const handleRemoveJobSource = async (sourceId) => {
    const next = jobSources.filter((source) => source.id !== sourceId);
    setJobSources(next);
    await persistJobSources(next);
  };

  const handleIndexOpenings = async () => {
    if (!company?.id || isIndexingOpenings) return;
    setIsIndexingOpenings(true);
    setOpeningsMessage({
      type: "info",
      text: "Scraping careers pages with Firecrawl and extracting open roles…",
    });
    try {
      const data = await requestCompanyTeam("/api/companyJobPostings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyId: company.id, action: "index" }),
      });
      setJobSources(normalizeSources(data.sources || []));
      setOpenings(
        (data.openings || []).map((row) => normalizeOpening(row)).filter(Boolean)
      );
      const errorNote =
        Array.isArray(data.errors) && data.errors.length
          ? ` (${data.errors.length} source ${
              data.errors.length === 1 ? "warning" : "warnings"
            })`
          : "";
      setOpeningsMessage({
        type: data.errors?.length && !data.addedCount ? "error" : "success",
        text: `${data.message || "Indexing finished."}${errorNote}`,
      });
    } catch (error) {
      setOpeningsMessage({ type: "error", text: error.message });
    } finally {
      setIsIndexingOpenings(false);
    }
  };

  const summary = useMemo(() => summarizeTeam([], openings), [openings]);

  const sortedOpenings = useMemo(
    () =>
      [...openings].sort((a, b) => {
        if (Boolean(a.isRd) !== Boolean(b.isRd)) return a.isRd ? -1 : 1;
        return (b.salary || 0) - (a.salary || 0);
      }),
    [openings]
  );

  const companyLogoUrl = useMemo(() => {
    if (company?.fields?.LogoUrl) return company.fields.LogoUrl;
    const logoDomain = domain || company?.fields?.Domain || "";
    return logoDomain ? `https://logo.clearbit.com/${logoDomain}` : "";
  }, [company?.fields?.LogoUrl, company?.fields?.Domain, domain]);

  return (
    <div className="col-span-full min-w-0 w-full space-y-6">
      <CompanyTeamAllocationPanel company={company} variant="default" />

      <div id="company-job-postings" className="scroll-mt-24 pt-8">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold leading-7 text-gray-900">Open Job Postings</h3>
            <p className="mt-1 text-sm text-gray-500">
              Index careers pages with Firecrawl. Likely R&D titles use a{" "}
              {Math.round(RD_MIN_RATE * 100)}–{Math.round(RD_MAX_RATE * 100)}% market-salary band.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => loadOpenings({ isManual: true })}
              disabled={!company?.id || isOpeningsRefreshing || isIndexingOpenings}
              className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Refresh openings"
              title="Reload openings from Airtable"
            >
              <ArrowPathIcon
                className={`h-4 w-4 ${isOpeningsRefreshing ? "animate-spin" : ""}`}
              />
            </button>
            <button
              type="button"
              onClick={handleIndexOpenings}
              disabled={!company?.id || isIndexingOpenings}
              className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isIndexingOpenings ? "Indexing…" : "Index openings"}
            </button>
          </div>
        </div>
        <hr className="mb-4 border-t border-gray-200" />

        <div className="mb-4 rounded-xl border border-gray-100 bg-white p-4 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Job posting source URL
              </label>
              <input
                className={INPUT_CLASS}
                value={sourceDraft}
                onChange={(e) => setSourceDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddJobSource();
                  }
                }}
                placeholder={
                  suggestedCareersUrl || "https://company.com/careers"
                }
              />
            </div>
            <button
              type="button"
              onClick={handleAddJobSource}
              disabled={!company?.id || isSavingSources || !sourceDraft.trim()}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <PlusIcon className="h-4 w-4" />
              {isSavingSources ? "Saving…" : "Add source"}
            </button>
          </div>
          {jobSources.length ? (
            <ul className="mt-3 flex flex-wrap gap-2">
              {jobSources.map((source) => (
                <li
                  key={source.id}
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 py-1 pl-2.5 pr-1 text-xs font-medium text-gray-700"
                >
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="truncate max-w-[220px] hover:text-gray-900"
                    title={source.url}
                  >
                    {source.label || source.kind}: {source.url.replace(/^https?:\/\//, "")}
                  </a>
                  <button
                    type="button"
                    onClick={() => handleRemoveJobSource(source.id)}
                    className="rounded-full p-1 text-gray-400 transition-colors hover:bg-white hover:text-red-600"
                    aria-label={`Remove ${source.url}`}
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-sm text-gray-500">
              No sources yet
              {suggestedCareersUrl ? (
                <>
                  {" "}
                  — Index openings will try{" "}
                  <span className="font-medium text-gray-700">{suggestedCareersUrl}</span>{" "}
                  and /jobs if empty.
                </>
              ) : (
                "."
              )}
            </p>
          )}
        </div>

        {openingsError ? (
          <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">
            <p className="font-medium">Could not load open roles</p>
            <p className="mt-1 break-words">{openingsError}</p>
          </div>
        ) : null}
        {openingsMessage ? (
          <div
            className={[
              "mb-4 rounded-lg px-4 py-3 text-sm break-words",
              openingsMessage.type === "success" ? "bg-emerald-50 text-emerald-700" : "",
              openingsMessage.type === "info" ? "bg-gray-50 text-gray-700" : "",
              openingsMessage.type === "error" ? "bg-red-50 text-red-600" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {openingsMessage.text}
          </div>
        ) : null}

        <div className="rounded-xl border border-gray-100 bg-white p-5 sm:p-6">
          {isOpeningsLoading ? (
            <div className="animate-pulse space-y-3">
              <div className="h-4 w-1/3 rounded bg-gray-100" />
              <div className="h-12 rounded-lg bg-gray-100" />
              <div className="h-12 rounded-lg bg-gray-100" />
            </div>
          ) : !sortedOpenings.length ? (
            <div className="flex flex-col items-center justify-center py-14 text-center">
              <CompanyLogoMark
                logoUrl={companyLogoUrl}
                companyName={company?.fields?.Name}
              />
              <p className="mt-4 text-sm font-medium text-gray-900">No open roles indexed yet</p>
              <p className="mt-1 text-sm text-gray-500">
                Add a careers URL, then Index openings. Band reference:{" "}
                {formatSalary(rdMinAmount(100000))}–{formatSalary(rdMaxAmount(100000))} on a $100k
                salary for likely R&D titles.
              </p>
              <button
                type="button"
                onClick={handleIndexOpenings}
                disabled={!company?.id || isIndexingOpenings}
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {isIndexingOpenings ? "Indexing…" : "Index openings"}
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="pb-3 pl-1 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      Role
                    </th>
                    <th className="pb-3 px-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      Location
                    </th>
                    <th className="pb-3 px-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">
                      Market salary
                    </th>
                    <th className="pb-3 px-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">
                      R&D band
                    </th>
                    <th className="pb-3 pr-1 text-right text-xs font-medium uppercase tracking-wide text-gray-500">
                      Match
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {sortedOpenings.map((role) => (
                    <tr key={role.id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3.5 pr-3 pl-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900">
                            {role.title}
                          </p>
                          {role.url ? (
                            <a
                              href={role.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="shrink-0 text-gray-400 hover:text-gray-700 transition-colors"
                              aria-label={`Open posting for ${role.title}`}
                            >
                              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                            </a>
                          ) : null}
                        </div>
                      </td>
                      <td className="py-3.5 px-2 text-sm text-gray-600">
                        {role.location || "—"}
                      </td>
                      <td className="py-3.5 px-2 text-right text-sm font-medium tabular-nums text-gray-900">
                        {formatSalary(role.salary)}
                      </td>
                      <td className="py-3.5 px-2 text-right text-sm tabular-nums text-gray-600">
                        {role.isRd && role.salary != null
                          ? `${formatSalary(rdMinAmount(role.salary))}–${formatSalary(
                              rdMaxAmount(role.salary)
                            )}`
                          : "—"}
                      </td>
                      <td className="py-3.5 pl-2 pr-1 text-right">
                        <RdBadge isRd={Boolean(role.isRd)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-gray-200">
                    <td className="pt-4 pl-1 text-sm font-medium text-gray-900" colSpan={3}>
                      Open R&D payroll band (visible roles)
                    </td>
                    <td className="pt-4 px-2 text-right text-sm font-semibold tabular-nums text-emerald-700">
                      {formatSalary(summary.openRdMinTotal)}–{formatSalary(summary.openRdMaxTotal)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CompanyTeamSection;
